// Klapp- und Sichtbarkeitsregeln des Kennzahlen-Katalogs.
//
// Bewusst getrennt von `KennzahlenTab.tsx`: die Regeln sind reine Katalog-Arithmetik
// ohne React, Netzwerk oder DOM. Im Reiter selbst hingen sie zwischen 2000 Zeilen
// Render-Code und ließen sich nur über die komplette Komponente ansteuern — samt
// Fluent-UI-Import und Kontakt-Fetch, für eine Frage, die nur „welche Zeilen bleiben
// stehen?" lautet.

import type { FieldRowDef, RowDef } from "./kpiTypes";

/**
 * Vorfahrenkette je Zeile: die eigene Gruppe, deren Gruppe, und so weiter.
 *
 * EINMAL beim Modulstart gebaut und nicht je Zeile im Render hochgelaufen — die
 * Sichtbarkeitsregel prüft bei jedem Tastendruck rund 80 Zeilen, während der
 * Katalog selbst statisch ist und sich zur Laufzeit gar nicht ändern kann.
 */
export const buildGroupAncestors = (rows: readonly RowDef[]): Map<string, string[]> => {
  const parentOf = new Map<string, string>();
  for (const r of rows) {
    if (r.kind !== "section" && r.groupId) parentOf.set(r.id, r.groupId);
  }
  const out = new Map<string, string[]>();
  for (const id of parentOf.keys()) {
    const kette: string[] = [];
    // Der `includes`-Riegel fängt einen im Katalog verdrehten `groupId`-Verweis
    // ab: ein Zyklus ließe die Schleife sonst endlos laufen — und zwar beim
    // Laden des Moduls, der Reiter käme nie zum ersten Render.
    for (let g = parentOf.get(id); g && !kette.includes(g); g = parentOf.get(g)) {
      kette.push(g);
    }
    out.set(id, kette);
  }
  return out;
};

/**
 * Feld-Kinder je Gruppe — direkte UND indirekte, weil die Vorfahrenkette jede
 * Ebene enthält. Basis des „n erfasst"-Zählers; Header-Zeilen zählen nicht mit,
 * sie tragen selbst keinen Wert.
 */
export const buildGroupFieldChildren = (
  rows: readonly RowDef[],
  ancestors: ReadonlyMap<string, string[]>,
): Map<string, FieldRowDef[]> => {
  const out = new Map<string, FieldRowDef[]>();
  for (const row of rows) {
    if (row.kind !== "field") continue;
    for (const g of ancestors.get(row.id) ?? []) {
      const liste = out.get(g);
      if (liste) liste.push(row);
      else out.set(g, [row]);
    }
  }
  return out;
};

/**
 * Alles, was sich zuklappen lässt: Sektionen UND Untergruppen.
 *
 * Beides zusammen, weil `collapsed` eine gemeinsame Id-Menge ist — nähme der
 * Startzustand (und „Keine") nur die Sektions-Ids, stünden „Verteilung auf
 * Länder" und die Tätigkeitsbereiche beim Aufklappen einer Sektion sofort
 * wieder offen da, also genau das, was der Nutzer nicht sehen will.
 */
export const buildCollapsibleIds = (rows: readonly RowDef[]): string[] =>
  rows.filter((r) => r.kind === "section" || r.kind === "header").map((r) => r.id);

export type RowVisibility = {
  /** Zugeklappte Sektionen UND Untergruppen in einer Menge. */
  collapsed: ReadonlySet<string>;
  /** Ergebnis von `buildGroupAncestors` — vorberechnet, nicht je Zeile neu. */
  ancestors: ReadonlyMap<string, string[]>;
  /**
   * Trägt die Zeile in IRGENDEINEM sichtbaren Jahr einen Wert? Als Prädikat
   * hereingereicht, weil die Antwort im Verbund aus den aggregierten Firmen
   * kommt und einzeln aus dem Jahres-Datensatz — zwei Quellen, die hier nichts
   * verloren haben. Wird nur für `hideWhenEmpty`-Zeilen überhaupt gefragt.
   */
  hasValue: (row: FieldRowDef) => boolean;
};

export const isRowVisible = (row: RowDef, v: RowVisibility): boolean => {
  if (row.kind === "section") return true;
  // Sektion eingeklappt → alle child-Zeilen aus
  if (v.collapsed.has(row.sectionId)) return false;
  // Transitiv über die VORFAHREN, nicht nur über die direkte Gruppe: sonst
  // bliebe „Tätigkeitsbereiche" samt seiner fünf Zeilen stehen, wenn man das
  // darüberliegende „Freelancer" zuklappt.
  const vorfahren = v.ancestors.get(row.id);
  if (vorfahren?.some((g) => v.collapsed.has(g))) return false;
  // Header-Zeilen sind selbst klickbare Gruppenköpfe und bleiben sichtbar,
  // solange keine ihrer übergeordneten Gruppen zu ist.
  if (row.kind === "header") return true;
  // `hideWhenEmpty` gilt nur für Zeilen OHNE Gruppe — innerhalb einer Gruppe
  // übernimmt die Gruppe das Verstecken: ist sie zu, ist die Zeile ohnehin weg;
  // ist sie offen, hat der Nutzer sie ausdrücklich geöffnet und will alle Zeilen
  // sehen. Vorher waren die Tätigkeitsbereiche unsichtbar, SOLANGE sie leer
  // waren — sie ließen sich über den Reiter also nie befüllen, weil das Feld
  // erst auftauchte, wenn schon ein Wert drinstand.
  if (row.hideWhenEmpty && !vorfahren?.length && !v.hasValue(row)) return false;
  return true;
};

export const filterVisibleRows = (rows: readonly RowDef[], v: RowVisibility): RowDef[] =>
  rows.filter((r) => isRowVisible(r, v));

/**
 * „n erfasst" der eingeklappten Gruppenzeile: wie viele ihrer Feld-Kinder für
 * dieses Jahr einen Wert tragen. Ohne den Zähler sieht man einer zugeklappten
 * Gruppe nicht an, ob dort überhaupt etwas liegt, und klappt reihum alles auf.
 *
 * Zählt über `buildGroupFieldChildren`, also inklusive der Enkel: „Freelancer"
 * zugeklappt verschluckt auch die Tätigkeitsbereiche, deren Werte müssen sich
 * deshalb in seiner Zahl wiederfinden.
 */
export const countGroupFilled = (
  headerId: string,
  children: ReadonlyMap<string, FieldRowDef[]>,
  hasValue: (row: FieldRowDef) => boolean,
): number => (children.get(headerId) ?? []).filter(hasValue).length;

/** Kein Zähler in dieser Jahresspalte. */
type KeinZaehler = null;

/**
 * Zählertexte der eingeklappten Gruppenzeile — EINER JE JAHRESSPALTE, in der
 * Reihenfolge von `years`.
 *
 * Je Spalte, weil die Zeile im Jahresraster steht: ein einzelner Zähler in der
 * ersten Spalte sprach über das ÄLTESTE Jahr, las sich neben dem Gruppenlabel
 * aber wie eine Aussage über die ganze Gruppe. Eine nur für das laufende Jahr
 * gefüllte Gruppe meldete so „0 erfasst" — der Nutzer klappte nicht auf und die
 * Werte blieben unauffindbar, seit die Gruppen standardmäßig zu sind.
 *
 * Leere Spalten bleiben leer statt „0 erfasst" zu zeigen: sonst stünden bei
 * einem frisch angelegten Kontakt vier Nullen in jeder Gruppenzeile. Ist die
 * Gruppe über ALLE sichtbaren Jahre leer, sagt das die erste Spalte einmal
 * ausdrücklich — schweigen dürfte sie hier nicht, „nichts" und „nicht geprüft"
 * wären sonst dasselbe Bild.
 */
export const groupCountLabels = (
  headerId: string,
  children: ReadonlyMap<string, FieldRowDef[]>,
  years: readonly number[],
  hasValue: (row: FieldRowDef, year: number) => boolean,
): (string | KeinZaehler)[] => {
  const anzahlen = years.map((y) =>
    countGroupFilled(headerId, children, (r) => hasValue(r, y)),
  );
  if (anzahlen.every((n) => n === 0)) {
    return anzahlen.map((_, i) => (i === 0 ? "nichts erfasst" : null));
  }
  return anzahlen.map((n) => (n > 0 ? `${n} erfasst` : null));
};
